import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '@fontsource/exo-2';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OrderDesign from '../OrderDesign';
import { API_URL } from '../config/apiConfig';
import { classifyOrderForStrategyStatistics } from '../utils/attackStrategyStatistics';
import './OrderScreenshotGallery.css';

const BASE_FACTIONS = [
  'Remnants of Humanity',
  'Exolot Dominion',
  'League of Shadows',
  "Ra's Children",
  'Asrah',
  'The Conspiracy',
  'The Corrupted Core',
  'The Covenant',
  'The Divine Syndicate',
  'The Forsaken Pact',
  'The Hollow',
  'The Iron Alliance',
  'The Last Crusade',
  'The Shadow Consortium',
  'The Silent Accord',
  'The Sentients',
];

const CARD_SCALE_OPTIONS = [
  { label: '1× (390px)', value: 1 },
  { label: '1.5× (585px)', value: 1.5 },
  { label: '2× (780px)', value: 2 },
  { label: '2.5× (975px)', value: 2.5 },
  { label: '3× (1170px)', value: 3 },
];

const BACKGROUND_OPTIONS = [
  { id: 'dark', label: 'Dark' },
  { id: 'black', label: 'Black' },
  { id: 'white', label: 'White' },
  { id: 'checker', label: 'Checker' },
];

const FAN_UNIT_COUNT = 5;
/** Fan is rendered 20% smaller than the manual scale (single-card view unchanged). */
const FAN_DISPLAY_SCALE = 0.8;
const FAN_OFFSET_Y_PX = -150;

function isUnitOrder(order) {
  return classifyOrderForStrategyStatistics(order) === 'unit';
}

function pickRandomUnitFan(sourceOrders, count = FAN_UNIT_COUNT) {
  const units = sourceOrders.filter(isUnitOrder);
  if (units.length < count) {
    throw new Error(
      `OrderScreenshotGallery: need at least ${count} units, found ${units.length}`,
    );
  }

  const pool = [...units];
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

function getFanCardTransform(index, total = FAN_UNIT_COUNT) {
  const center = (total - 1) / 2;
  const offset = index - center;
  const rotationDeg = offset * 12;
  const translateXPx = offset * 118;
  const translateYPx = Math.abs(offset) * 24;

  return {
    transform: `translateX(calc(-50% + ${translateXPx}px)) translateY(${translateYPx}px) rotate(${rotationDeg}deg)`,
    zIndex: index + 1,
  };
}

function normalizeSearchText(value) {
  return String(value ?? '').trim().toLowerCase();
}

function orderMatchesQuery(order, query) {
  if (!query) return true;
  const haystack = [
    order.name,
    order.id,
    order.faction_name,
    order.faction,
    order.type,
    order.rarity,
    order.cost,
  ]
    .map((part) => normalizeSearchText(part))
    .join(' ');
  return haystack.includes(query);
}

async function fetchAllOrders(language) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('OrderScreenshotGallery: login required (missing auth token)');
  }

  const normalizedLanguage = (language || 'en-us').toLowerCase().replace('_', '-');
  const results = await Promise.all(
    BASE_FACTIONS.map(async (faction) => {
      const response = await fetch(
        `${API_URL}/orders?faction=${encodeURIComponent(faction)}&lang=${encodeURIComponent(normalizedLanguage)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        throw new Error(`OrderScreenshotGallery: failed to fetch orders for ${faction} (${response.status})`);
      }
      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error(`OrderScreenshotGallery: invalid orders payload for ${faction}`);
      }
      return data;
    }),
  );

  const uniqueOrders = [];
  const seenIds = new Set();
  for (const order of results.flat()) {
    if (order?.id == null) {
      throw new Error('OrderScreenshotGallery: order missing id in catalog response');
    }
    if (!seenIds.has(order.id)) {
      seenIds.add(order.id);
      uniqueOrders.push(order);
    }
  }

  uniqueOrders.sort((a, b) => {
    const nameCompare = String(a.name || '').localeCompare(String(b.name || ''));
    if (nameCompare !== 0) return nameCompare;
    return Number(a.id) - Number(b.id);
  });

  return uniqueOrders;
}

function resolveBackgroundClass(backgroundId, screenshotMode) {
  if (screenshotMode && backgroundId === 'dark') {
    return 'order-screenshot-gallery--bg-black';
  }
  if (backgroundId === 'white') return 'order-screenshot-gallery--bg-white';
  if (backgroundId === 'checker') return 'order-screenshot-gallery--bg-checker';
  if (backgroundId === 'black') return 'order-screenshot-gallery--screenshot-mode';
  return '';
}

export default function OrderScreenshotGallery() {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchInputRef = useRef(null);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState(() => {
    const fromUrl = searchParams.get('order') || searchParams.get('id');
    return fromUrl != null && fromUrl !== '' ? Number(fromUrl) : null;
  });
  const [cardScale, setCardScale] = useState(2);
  const [backgroundId, setBackgroundId] = useState('dark');
  const [screenshotMode, setScreenshotMode] = useState(false);
  const [viewMode, setViewMode] = useState('single');
  const [fanOrders, setFanOrders] = useState(null);
  const [fanError, setFanError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const catalog = await fetchAllOrders(i18n.language);
        if (cancelled) return;
        setOrders(catalog);
        setSelectedOrderId((currentId) => {
          if (currentId != null && catalog.some((order) => Number(order.id) === Number(currentId))) {
            return currentId;
          }
          return catalog[0]?.id ?? null;
        });
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const normalizedQuery = normalizeSearchText(searchQuery);
  const filteredOrders = useMemo(
    () => orders.filter((order) => orderMatchesQuery(order, normalizedQuery)),
    [orders, normalizedQuery],
  );

  const selectedOrder = useMemo(
    () => orders.find((order) => Number(order.id) === Number(selectedOrderId)) ?? null,
    [orders, selectedOrderId],
  );

  const selectOrder = useCallback((orderId) => {
    setViewMode('single');
    setFanOrders(null);
    setFanError(null);
    setSelectedOrderId(orderId);
    setSearchParams({ order: String(orderId) }, { replace: true });
  }, [setSearchParams]);

  const createRandomFan = useCallback(() => {
    try {
      const pool = normalizedQuery ? filteredOrders : orders;
      setFanOrders(pickRandomUnitFan(pool, FAN_UNIT_COUNT));
      setViewMode('fan');
      setFanError(null);
    } catch (err) {
      setFanError(err instanceof Error ? err.message : String(err));
    }
  }, [filteredOrders, normalizedQuery, orders]);

  const moveSelection = useCallback((direction) => {
    if (filteredOrders.length === 0) return;
    const currentIndex = filteredOrders.findIndex(
      (order) => Number(order.id) === Number(selectedOrderId),
    );
    const nextIndex = currentIndex < 0
      ? (direction < 0 ? filteredOrders.length - 1 : 0)
      : (currentIndex + direction + filteredOrders.length) % filteredOrders.length;
    selectOrder(filteredOrders[nextIndex].id);
  }, [filteredOrders, selectedOrderId, selectOrder]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        if (event.key === 'Escape') {
          event.target.blur();
        }
        return;
      }

      if (event.key === '/') {
        event.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveSelection(1);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveSelection(-1);
        return;
      }

      if (event.key === 's' || event.key === 'S') {
        event.preventDefault();
        setScreenshotMode((value) => !value);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [moveSelection]);

  const rootClassName = [
    'order-screenshot-gallery',
    screenshotMode ? 'order-screenshot-gallery--screenshot-mode' : '',
    resolveBackgroundClass(backgroundId, screenshotMode),
  ].filter(Boolean).join(' ');

  if (loading) {
    return <div className="order-screenshot-gallery order-screenshot-gallery-loading">Loading order catalog…</div>;
  }

  if (loadError) {
    return <div className="order-screenshot-gallery order-screenshot-gallery-error">{loadError}</div>;
  }

  return (
    <div className={rootClassName}>
      <header className="order-screenshot-gallery-header order-screenshot-gallery-chrome">
        <div>
          <h1 className="order-screenshot-gallery-title">Order screenshot gallery</h1>
          <p className="order-screenshot-gallery-subtitle">
            Dev-only · player1 · /dev/order-screenshots
          </p>
        </div>
        <div className="order-screenshot-gallery-controls">
          <label>
            Scale
            <select
              value={String(cardScale)}
              onChange={(event) => setCardScale(Number(event.target.value))}
            >
              {CARD_SCALE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            Background
            <select
              value={backgroundId}
              onChange={(event) => setBackgroundId(event.target.value)}
            >
              {BACKGROUND_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            aria-pressed={viewMode === 'fan'}
            onClick={createRandomFan}
            title="Show 5 random deploy units in a hand fan"
          >
            Random fan (5)
          </button>
          <button
            type="button"
            aria-pressed={screenshotMode}
            onClick={() => setScreenshotMode((value) => !value)}
            title="Hide chrome for clean screenshots (S)"
          >
            {screenshotMode ? 'Exit screenshot mode' : 'Screenshot mode (S)'}
          </button>
        </div>
        {fanError ? (
          <p className="order-screenshot-gallery-fan-error order-screenshot-gallery-chrome" role="alert">
            {fanError}
          </p>
        ) : null}
      </header>

      <div className="order-screenshot-gallery-body">
        <aside className="order-screenshot-gallery-sidebar order-screenshot-gallery-chrome">
          <div className="order-screenshot-gallery-search-wrap">
            <input
              ref={searchInputRef}
              className="order-screenshot-gallery-search"
              type="search"
              placeholder="Search name, id, faction, type…  (press /)"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>
          <div className="order-screenshot-gallery-list-meta">
            {filteredOrders.length} of {orders.length} orders
          </div>
          <ul className="order-screenshot-gallery-list">
            {filteredOrders.map((order) => (
              <li key={order.id}>
                <button
                  type="button"
                  className="order-screenshot-gallery-list-item"
                  aria-current={Number(order.id) === Number(selectedOrderId) ? 'true' : undefined}
                  onClick={() => selectOrder(order.id)}
                >
                  <span className="order-screenshot-gallery-list-item-name">{order.name}</span>
                  <span className="order-screenshot-gallery-list-item-meta">
                    #{order.id} · {order.faction_name || order.faction || 'Unknown'} · {order.type || 'order'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <main className="order-screenshot-gallery-stage-wrap">
          {viewMode === 'fan' && fanOrders && fanOrders.length > 0 ? (
            <>
              <div className="order-screenshot-gallery-stage-meta order-screenshot-gallery-chrome">
                <h2>Random fan · {fanOrders.length} units</h2>
                <p>
                  {fanOrders.map((order) => `${order.name} (#${order.id})`).join(' · ')}
                </p>
              </div>
              <div
                className="order-screenshot-gallery-fan-stage"
                style={{
                  transform: `translateY(${FAN_OFFSET_Y_PX}px) scale(${cardScale * FAN_DISPLAY_SCALE})`,
                }}
              >
                <div className="order-screenshot-gallery-fan">
                  {fanOrders.map((order, index) => (
                    <div
                      key={order.id}
                      className="order-screenshot-gallery-fan-card"
                      style={getFanCardTransform(index, fanOrders.length)}
                    >
                      <OrderDesign
                        order={order}
                        count={0}
                        minimal={false}
                        lazyLoad={false}
                        showOwnedCounter={false}
                        imageDraggable={false}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : selectedOrder ? (
            <>
              <div className="order-screenshot-gallery-stage-meta order-screenshot-gallery-chrome">
                <h2>{selectedOrder.name}</h2>
                <p>
                  ID {selectedOrder.id} · {selectedOrder.faction_name || selectedOrder.faction}
                  {' · '}{selectedOrder.rarity || 'common'} · cost {selectedOrder.cost}
                </p>
              </div>
              <div
                className="order-screenshot-gallery-card-stage"
                style={{ transform: `scale(${cardScale})` }}
              >
                <OrderDesign
                  key={selectedOrder.id}
                  order={selectedOrder}
                  count={0}
                  minimal={false}
                  lazyLoad={false}
                  showOwnedCounter={false}
                  imageDraggable={false}
                />
              </div>
            </>
          ) : (
            <div className="order-screenshot-gallery-empty">No order selected</div>
          )}
        </main>
      </div>

      {screenshotMode ? (
        <div className="order-screenshot-gallery-screenshot-hint order-screenshot-gallery-chrome">
          Press S to show controls
        </div>
      ) : null}
    </div>
  );
}
